import { useEffect, useMemo, useState } from "react";
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import { useTranslation } from 'react-i18next';
import { ListOptions } from "../../models/interfaces/list-options";
import { DateTime } from "luxon";
import { Group } from "../../models/interfaces/group";
import { Tag } from "../../models/interfaces/tag";
import { DetailFilterState } from "../../models/interfaces/detail-filter-state";
import { Button } from "../button/button";
import ReportDataUtil from "../../utilities/report-data-util";
import Select from "react-select";
import { ReportUserMovementItem } from "../../models/interfaces/reports/report-user-movement-item";
import { NumberUtils } from "../../utilities/number-utils";
import { GroupIds } from "../../models/enumerations/group-ids";
import MskScoreUtil from "../../utilities/msk-score-util";
import { AssociatedMovementAndBreathing } from "../../models/interfaces/associated-movement-and-breathing";
import { PainFilterOption, PainFilterOptionToTranslationKey } from "../../models/enumerations/pain-filter-option";
import ReportDynamicTable, { TableColumn } from "./report-dynamic-table";
import { Loader } from "../loader/loader";
import ReportFilterMenu from "../report-filter-menu/report-filter-menu";
import { ReportFilterDefinition } from "../../models/interfaces/report-filter-definition";
import { ReportFilterType } from "../../models/enumerations/report-filter-type";
import { MskHealthRiskOptions } from "../../models/msk-health-risk";
import { MovementFocusOptions } from "../../models/movement-focus-group";
import { ReportDynamicMovementData } from "../../models/interfaces/reports/report-dynamic-movement-data";

const COMPONENT_CLASS = "c-report-movement-dynamic";

const DEFAULT_FILTERS: DetailFilterState = {
    groupIds: [],
    tagIds: [],
    healthCategories: [],
    focusAreas: [],
    inPain: PainFilterOption.ALL,
};

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const ReportMovementDynamic: React.FC = () => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const { t, i18n } = useTranslation();
    const { state } = useAuthState();

    const painOptions = useMemo(() =>
        Object.values(PainFilterOption).map(value => ({
            value,
            label: t(PainFilterOptionToTranslationKey[value])
        })),
        [t]);

    const reportMonthOptions = useMemo<ListOptions[]>(() => {
        const options: ListOptions[] = [
            {
                value: DateTime.now().startOf("month").toISODate()!,
                label: t('reports.currentMonth')
            }
        ];

        if (!state.organization?.created) return options;

        let monthDate = DateTime.now().minus({ month: 1 });

        const orgCreatedDate = DateTime
            .fromJSDate(new Date(state.organization.created.seconds * 1000))
            .startOf('month');

        const monthDifference = DateTime.now()
            .startOf('month')
            .diff(orgCreatedDate, 'months')
            .months;

        while (options.length <= monthDifference) {
            options.push({
                value: monthDate.startOf("month").toISODate()!,
                label: monthDate.setLocale(i18n.language).toFormat("MMMM yyyy"),
            });

            monthDate = monthDate.minus({ month: 1 });
        }

        return options;
    }, [t, state.organization.created, i18n.language]);

    const [reportData, setReportData] = useState<ReportDynamicMovementData | null>(null);
    const [filteredReportData, setFilteredReportData] = useState<ReportDynamicMovementData | null>(null);
    const [filters, setFilters] = useState<DetailFilterState>(DEFAULT_FILTERS);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [selectedReportMonth, setSelectedReportMonth] = useState<string>(
        reportMonthOptions[0]?.value
    );
    const [groups, setGroups] = useState<Group[]>([]);
    const [tags, setTags] = useState<Tag[]>([]);

    const columns = useMemo<TableColumn<ReportUserMovementItem>[]>(() => [
        {
            key: "name",
            header: t('reports.movement.name'),
            sortable: true,
            width: 220,
            fontWeight: 400,
            render: row => (
                <a
                    href={`${window.location.origin}/users/${row.id}`}
                    target="_blank"
                    rel="noreferrer"
                >
                    {row.name}
                </a>
            )
        },
        {
            key: "toeTouchScore",
            header: t('reports.movement.toeTouchShort'),
            sortable: true,
            render: row => (
                <span className={row.toeTouchPain ? "-warning" : ""}>
                    {NumberUtils.formatNumber(row.toeTouchScore)}
                </span>
            ),
            sortAccessor: row => row.toeTouchScore ?? 0,
            width: 60
        },
        {
            key: "shoulderMobilityScore",
            header: t('reports.movement.shoulderShort'),
            sortable: true,
            render: row => (
                <span className={row.shoulderMobilityPain ? "-warning" : ""}>
                    {NumberUtils.formatNumber(row.shoulderMobilityScore)}
                </span>
            ),
            sortAccessor: row => row.shoulderMobilityScore ?? 0,
            width: 60
        },
        {
            key: "rotationScore",
            header: t('reports.movement.rotationShort'),
            sortable: true,
            render: row => (
                <span className={row.rotationPain ? "-warning" : ""}>
                    {NumberUtils.formatNumber(row.rotationScore)}
                </span>
            ),
            sortAccessor: row => row.rotationScore ?? 0,
            width: 60
        },
        {
            key: "balanceScore",
            header: t('reports.movement.balanceShort'),
            sortable: true,
            render: row => (
                <span className={row.balancePain ? "-warning" : ""}>
                    {NumberUtils.formatNumber(row.balanceScore)}
                </span>
            ),
            sortAccessor: row => row.balanceScore ?? 0,
            width: 60
        },
        {
            key: "squatScore",
            header: t('reports.movement.squatShort'),
            sortable: true,
            render: row => (
                <span className={row.squatPain ? "-warning" : ""}>
                    {NumberUtils.formatNumber(row.squatScore)}
                </span>
            ),
            sortAccessor: row => row.squatScore ?? 0,
            width: 60
        },
        {
            key: "shoulderClearing",
            header: t('reports.movement.shoulderClearingShort'),
            sortable: true,
            render: row => (
                <span className={
                    MskScoreUtil.isLow(GroupIds.SHOULDER_CLEARING, undefined, row.shoulderClearing)
                        ? "-warning"
                        : ""
                }>
                    {row.shoulderClearing ? <span className="text-red-dark">{t('general.fail')}</span> : t('general.pass')}
                </span>
            ),
            sortAccessor: row => Number(row.shoulderClearing),
            width: 60
        },
        {
            key: "spineClearing",
            header: t('reports.movement.spineClearingShort'),
            sortable: true,
            render: row => (
                <span className={
                    MskScoreUtil.isLow(GroupIds.SPINE_CLEARING, undefined, row.spineClearing)
                        ? "-warning"
                        : ""
                }>
                    {row.spineClearing ? <span className="text-red-dark">{t('general.fail')}</span> : t('general.pass')}
                </span>
            ),
            sortAccessor: row => Number(row.spineClearing),
            width: 60
        },
        {
            key: "movementScore",
            header: t('reports.movement.total'),
            sortable: true,
            render: row => (
                <span className="font-bold">
                    {NumberUtils.formatNumber(row.movementScore)}
                </span>
            ),
            sortAccessor: row => row.movementScore ?? 0,
            width: 60,
            boldContentText: true
        },
        {
            key: "movementFocus",
            header: t('reports.movement.focus'),
            sortable: true,
            width: 100
        }
    ], [t]);

    const filterDefinitions: ReportFilterDefinition[] = useMemo(() => [
        {
            key: "groupIds",
            label: t('reports.filterOptions.groups'),
            placeholder: t('reports.filterOptions.allGroups'),
            type: ReportFilterType.MultiSelect,
            options: groups.map(g => ({ label: g.name, value: g.id }))
        },
        {
            key: "tagIds",
            label: t('reports.filterOptions.tags'),
            placeholder: t('reports.filterOptions.allTags'),
            type: ReportFilterType.MultiSelect,
            options: tags.map(t => ({ label: t.name, value: t.id }))
        },
        {
            key: "healthCategories",
            label: t('reports.filterOptions.healthCategories'),
            placeholder: t('reports.filterOptions.allCategories'),
            type: ReportFilterType.MultiSelect,
            options: MskHealthRiskOptions
        },
        {
            key: "focusAreas",
            label: t('reports.filterOptions.focusAreas'),
            placeholder: t('reports.filterOptions.allFocusAreas'),
            type: ReportFilterType.MultiSelect,
            options: MovementFocusOptions
        },
        {
            key: "inPain",
            label: t("reports.filterOptions.pain"),
            type: ReportFilterType.SingleSelect,
            options: painOptions
        }
    ], [groups, tags, painOptions, t]);

    useEffect(() => {
        const orgId = state.organization?.id;
        if (!orgId) return;

        const load = async () => {
            const { groups, tags } = await ReportDataUtil.loadGroupsAndTags(orgId);
            setGroups(groups);
            setTags(tags);
        };

        load();
    }, [state.organization?.id]);

    useEffect(() => {
        if (!reportData) return;
        const filtered = applyFilters(reportData, filters, searchQuery);
        setFilteredReportData(filtered);
    }, [filters, reportData, searchQuery]);

    useEffect(() => {
        const orgId = state.organization?.id;
        if (!orgId || !selectedReportMonth) return;

        setReportData(null);
        setFilteredReportData(null);

        setFilters(DEFAULT_FILTERS);
        setSearchQuery("");

        const loadData = async () => {
            const data = await ReportDataUtil.grabInitialMovementData(orgId, selectedReportMonth);
            setReportData(data);
            setFilteredReportData(data);
        };

        loadData();
    }, [selectedReportMonth, state.organization?.id]);

    useEffect(() => {
        setIsLoading(!reportData);
    }, [reportData]);

    const applyFilters = (
        reportData: ReportDynamicMovementData,
        filters: DetailFilterState,
        searchQuery: string
    ): ReportDynamicMovementData => {

        const filteredUsers = reportData.users.filter(user => {
            if (searchQuery &&
                !user.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;

            if (filters.groupIds.length > 0 &&
                (!user.groupId || !filters.groupIds.includes(user.groupId))) return false;

            if (filters.tagIds.length > 0 &&
                !user.tags?.some(tag => filters.tagIds.includes(tag.id))) return false;

            if (filters.healthCategories.length > 0 &&
                (!user.healthCategory ||
                    !filters.healthCategories.includes(user.healthCategory))) return false;

            if (filters.inPain === PainFilterOption.YES && !user.inPain) return false;
            if (filters.inPain === PainFilterOption.NO && user.inPain) return false;

            if (filters.focusAreas.length > 0) {
                const expanded = filters.focusAreas.flatMap(f => {
                    const b = AssociatedMovementAndBreathing[f];
                    return b ? [f, b] : [f];
                });

                if (!user.movementFocusValue ||
                    !expanded.includes(user.movementFocusValue)) return false;
            }

            return true;
        });

        const metrics = ReportDataUtil.calculateMovementMetrics(filteredUsers);

        return {
            ...reportData,
            users: filteredUsers,
            ...metrics
        };

    };

    const handleExportCsv = () => {
        if (!filteredReportData?.users) return;

        const csvString = ReportDataUtil.generateMovement({
            organizationName: reportData?.organizationName ?? '',
            reportDate: selectedReportMonth!,
            users: filteredReportData.users,
            totalUsers: reportData?.totalUsers ?? 0
        });

        const blob = new Blob(["\uFEFF" + csvString], {
            type: "text/csv;charset=utf-8;"
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `${t('reports.movement.fileReportName')}_${selectedReportMonth}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    };

    return (
        <div className={COMPONENT_CLASS}>
            <Loader isVisible={isLoading} />

            <div className={`${COMPONENT_CLASS}__header-container`}>
                <div className={`${COMPONENT_CLASS}__header`}>
                    <h1>{t('reportType.movement_health_rep')}</h1>
                    <p className={`label-large ${COMPONENT_CLASS}__sub-header`}>
                        {state.organization?.name} • {selectedReportMonth} • {filteredReportData?.users.length} {t('general.users')}
                    </p>
                </div>

                <div className={`${COMPONENT_CLASS}__actions`}>
                    <Button onClick={handleExportCsv} buttonText={t('buttons.btn_exportCSV')} type="link" />
                    <Button
                        disabled={!filteredReportData?.users}
                        onClick={() => ReportDataUtil.downloadMovement(
                            filteredReportData?.users!,
                            ReportDataUtil.buildReportDetailFilterPayload(groups, tags, filters),
                            state.organization?.id!,
                            reportData?.organizationName ?? '',
                            state.user?.id!,
                            selectedReportMonth!
                        )}
                        buttonText={t('buttons.btn_exportPDF')}
                        type="link"
                    />

                    <ReportFilterMenu
                        filters={filterDefinitions}
                        values={filters}
                        onChange={(key, value) =>
                            setFilters(prev => ({ ...prev, [key]: value }))
                        }
                    >
                        <label className={`${COMPONENT_CLASS}__label body-bold`}>
                            {t('reports.filterOptions.reportDate')}
                        </label>
                        <Select
                            options={reportMonthOptions}
                            value={reportMonthOptions.find(o => o.value === selectedReportMonth)}
                            onChange={(opt: any) => opt && setSelectedReportMonth(opt.value)}
                        />
                    </ReportFilterMenu>
                </div>
            </div>

            {
                filteredReportData && (
                    <div className={`${COMPONENT_CLASS}__summary`}>
                        <div className={`${COMPONENT_CLASS}__summary-items`}>
                            <div className={`${COMPONENT_CLASS}__summary-item`}>
                                <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>{filteredReportData?.toeTouchCount}</span>
                                <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}> {t('movementFocus.toeTouch')}</span>
                            </div>
                            <div className={`${COMPONENT_CLASS}__summary-item`}>
                                <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>{filteredReportData?.shoulderMobilityCount}</span>
                                <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}> {t('movementFocus.shoulderMobility')}</span>
                            </div>
                            <div className={`${COMPONENT_CLASS}__summary-item`}>
                                <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>{filteredReportData?.rotationCount}</span>
                                <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}> {t('movementFocus.rotation')}</span>
                            </div>
                            <div className={`${COMPONENT_CLASS}__summary-item`}>
                                <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>{filteredReportData?.squatCount}</span>
                                <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}> {t('movementFocus.squat')}</span>
                            </div>
                            <div className={`${COMPONENT_CLASS}__summary-item`}>
                                <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>{filteredReportData?.balanceCount}</span>
                                <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}> {t('movementFocus.shortLabel.balanceAndReach')}</span>
                            </div>
                            <div className={`${COMPONENT_CLASS}__summary-item`}>
                                <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>{filteredReportData?.shoulderClearingCount}</span>
                                <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}> {t('movementFocus.shoulderClearing')}</span>
                            </div>
                            <div className={`${COMPONENT_CLASS}__summary-item`}>
                                <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>{filteredReportData?.spineClearingCount}</span>
                                <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}> {t('movementFocus.spineClearing')}</span>
                            </div>
                            <div className={`${COMPONENT_CLASS}__summary-item`}>
                                <span className={`${COMPONENT_CLASS}__summary-item-count data-small`}>{filteredReportData?.usersInPainCount}</span>
                                <span className={`${COMPONENT_CLASS}__summary-item-label label-large`}> {t('reports.movement.usersInPain')}</span>
                            </div>
                        </div>
                    </div>
                )
            }

            {
                filteredReportData?.users && (
                    <div className={`${COMPONENT_CLASS}__table-container`}>
                        <div className={`${COMPONENT_CLASS}__table-header`}>
                            <h2>{t('reports.individualScores')}</h2>
                            <div className={`${COMPONENT_CLASS}__search-container`}>
                                <input
                                    className={`input-field ${COMPONENT_CLASS}__search-input`}
                                    type="text"
                                    placeholder={t('reports.filterOptions.searchByName')}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                        </div>

                        <ReportDynamicTable
                            rows={filteredReportData.users}
                            columns={columns}
                            pageSize={25}
                        />
                    </div>
                )
            }
        </div>
    );
};

export default ReportMovementDynamic;
